
import React, { useState, useEffect, useRef } from 'react';
import { X, Calculator, Plus, Save, Box, BarChart2 } from 'lucide-react';
import { Measure } from '@/api/datamodels';

interface WizardProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (measure: Measure) => void;
    onDelete?: (id: string) => void;
    initialData?: Measure;
    // Context from Data Model
    availableTables: { name: string; alias: string; columns?: string[] }[];
    existingMeasures: Measure[];
}

export const DataModelMeasureWizard: React.FC<WizardProps> = ({
    isOpen,
    onClose,
    onSave,
    onDelete,
    initialData,
    availableTables,
    existingMeasures
}) => {
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [expression, setExpression] = useState('');
    const [format, setFormat] = useState('number');

    useEffect(() => {
        if (isOpen) {
            setName(initialData?.name || '');
            setDescription(initialData?.description || '');
            setExpression(initialData?.expression || '');
            setFormat(initialData?.format || 'number');
        }
    }, [isOpen, initialData]);

    const [showSuggestions, setShowSuggestions] = useState(false);
    const [filteredSuggestions, setFilteredSuggestions] = useState<any[]>([]);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    const supportedFunctions = [
        { name: 'SUM', description: 'Calculates the sum of a column', type: 'function' },
        { name: 'AVG', description: 'Calculates the average of a column', type: 'function' },
        { name: 'COUNT', description: 'Counts the number of rows', type: 'function' },
        { name: 'MIN', description: 'Returns the smallest value', type: 'function' },
        { name: 'MAX', description: 'Returns the largest value', type: 'function' },
        // DuckDB Specifics
        { name: 'DATE_TRUNC', description: 'Truncates a timestamp to a specified precision', type: 'function' },
        { name: 'CASE', description: 'Conditional logic', type: 'function' }
    ];

    // Flatten available fields for suggestions
    // For MVP, we assume tables have columns. If not, we just show table aliases.
    const allFields = [
        ...existingMeasures.map(m => ({ name: m.name, type: 'measure', description: 'Existing Measure' })),
        ...availableTables.map(t => ({ name: t.alias, type: 'table', description: `Table: ${t.name}` }))
    ];

    const handleSave = () => {
        if (!name || !expression) return;
        onSave({
            id: initialData?.id, // Keep ID if editing
            name,
            description,
            expression,
            format
        });
        onClose();
    };

    const insertText = (text: string) => {
        if (!textareaRef.current) return;
        const val = expression;
        const pos = textareaRef.current.selectionStart;
        const newText = val.slice(0, pos) + text + val.slice(pos);
        setExpression(newText);
        textareaRef.current.focus();
        // set cursor after inserted text
        setTimeout(() => {
            textareaRef.current?.setSelectionRange(pos + text.length, pos + text.length);
        }, 0);
        setShowSuggestions(false);
    };

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const val = e.target.value;
        setExpression(val);

        // Simple autocomplete logic: check word before cursor
        const pos = e.target.selectionStart;
        const textBefore = val.slice(0, pos);
        const match = textBefore.match(/([a-zA-Z0-9_]+)$/);

        if (match) {
            const word = match[1].toLowerCase();
            const suggestions = [...supportedFunctions, ...allFields].filter(
                item => item.name.toLowerCase().startsWith(word)
            );
            if (suggestions.length > 0) {
                setFilteredSuggestions(suggestions);
                setShowSuggestions(true);
                setSelectedIndex(0);
                return;
            }
        }
        setShowSuggestions(false);
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl overflow-hidden border border-gray-200 flex flex-col max-h-[90vh]">
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gray-50">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-blue-100 rounded-lg">
                            <Calculator className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                            <h2 className="text-lg font-bold text-gray-800">
                                {initialData ? 'Edit Measure' : 'New Measure'}
                            </h2>
                            <p className="text-xs text-gray-500">
                                Define business logic using SQL/DuckDB syntax
                            </p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-gray-200 rounded-full">
                        <X className="w-5 h-5 text-gray-500" />
                    </button>
                </div>

                <div className="flex-1 overflow-auto p-6 space-y-6">
                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <label className="text-xs font-bold text-gray-500 uppercase">Name</label>
                            <input
                                className="w-full p-2 border rounded text-sm"
                                value={name}
                                onChange={e => setName(e.target.value)}
                                placeholder="e.g. Total Revenue"
                            />
                        </div>
                        <div className="space-y-2">
                            <label className="text-xs font-bold text-gray-500 uppercase">Description</label>
                            <input
                                className="w-full p-2 border rounded text-sm"
                                value={description}
                                onChange={e => setDescription(e.target.value)}
                                placeholder="Optional description"
                            />
                        </div>
                    </div>

                    <div className="flex gap-4 h-[300px]">
                        <div className="flex-1 flex flex-col space-y-2 relative">
                            <label className="flex justify-between text-xs font-bold text-gray-500 uppercase">
                                <span>Expression (DuckDB SQL)</span>
                                <span className="font-normal text-gray-400">Supports SQL functions</span>
                            </label>
                            <div className="relative flex-1">
                                <textarea
                                    ref={textareaRef}
                                    className="w-full h-full p-4 font-mono text-sm border rounded bg-gray-50 resize-none focus:ring-2 focus:ring-blue-500 outline-none"
                                    value={expression}
                                    onChange={handleChange}
                                    placeholder="SUM(orders.amount)"
                                />
                                {showSuggestions && (
                                    <div className="absolute left-2 bottom-10 z-50 w-64 bg-white border shadow-xl rounded max-h-48 overflow-auto">
                                        {filteredSuggestions.map((item, idx) => (
                                            <button
                                                key={idx}
                                                className={`w-full text-left px-3 py-2 text-xs flex items-center gap-2 hover:bg-blue-50 ${idx === selectedIndex ? 'bg-blue-50' : ''}`}
                                                onClick={() => {
                                                    // Replace current word
                                                    if (!textareaRef.current) return;
                                                    const val = expression;
                                                    const pos = textareaRef.current.selectionStart;
                                                    const textBefore = val.slice(0, pos);
                                                    const match = textBefore.match(/([a-zA-Z0-9_]+)$/);
                                                    const wordLen = match ? match[1].length : 0;

                                                    const newText = val.slice(0, pos - wordLen) + (item.type === 'function' ? item.name + '(' : item.name) + val.slice(pos);
                                                    setExpression(newText);
                                                    setShowSuggestions(false);
                                                    textareaRef.current.focus();
                                                }}
                                            >
                                                {item.type === 'function' ? <div className="w-4 h-4 bg-purple-100 text-purple-600 rounded flex items-center justify-center font-serif italic font-bold">f</div> : <Box className="w-4 h-4 text-blue-500" />}
                                                <span>{item.name}</span>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="w-64 border rounded bg-gray-50 flex flex-col">
                            <div className="p-2 border-b text-xs font-bold text-gray-500">Available Tables</div>
                            <div className="flex-1 overflow-auto p-2 space-y-2">
                                {availableTables.map(t => (
                                    <div key={t.alias} className="text-xs">
                                        <div className="font-semibold text-gray-700 flex items-center gap-1">
                                            <Box className="w-3 h-3" /> {t.alias}
                                        </div>
                                        {/* If we had columns, list them here */}
                                    </div>
                                ))}
                                {availableTables.length === 0 && <div className="text-xs text-gray-400 italic">No tables in model</div>}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="p-4 bg-gray-50 border-t flex justify-between gap-2">
                    <div>
                        {initialData && onDelete && (
                            <button
                                onClick={() => {
                                    if (initialData.id) onDelete(initialData.id);
                                    onClose();
                                }}
                                className="px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 hover:text-red-700 rounded border border-transparent hover:border-red-200 transition-colors"
                            >
                                Delete Measure
                            </button>
                        )}
                    </div>
                    <div className="flex gap-2">
                        <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-200 rounded">Cancel</button>
                        <button onClick={handleSave} className="px-4 py-2 text-sm font-bold text-white bg-blue-600 rounded hover:bg-blue-700 flex items-center gap-2">
                            <Save className="w-4 h-4" /> Save Measure
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};
